/* Imports */
// Library
import {
  select, scaleBand, scaleLinear, axisTop, range,
  easePolyInOut, area, curveCardinal, ascending,
} from 'd3'

// Local
import { generateDataVals, getAreaData, getAverages, getIndex } from '../../globals/helpers'

// components
import Toggle from '../Toggle/Toggle';
import DropDown from '../Dropdown/dropDown'
import Gradient from '../Gradient/gradient'

// constants
import { CLASSES as C, CONFIG, COLORS, COLUMNS, TOGGLE_VALS, SPACING } from '../../globals/constants';
// styles
import './style.scss';

export default class DotChart {
  constructor(data) {
    /* Selections */
    this.selection = select('#dot-chart')
      .append('div') // append scrollama step container
      .attr('class', `${C.STEP}`)

    /* Config */
    this.currentColumn = TOGGLE_VALS[0].text
    this.toggleVals = TOGGLE_VALS
    this.control = COLUMNS.sort(ascending)[0]
    this.dropDownVals = COLUMNS

    /* Data */
    this.data = data
    this.nodeData = generateDataVals(data, this.control)
    this.areaData = getAreaData(data, this.currentColumn, this.control)
    this.averages = getAverages(data, this.currentColumn, this.control)

    /* D3 Scales */
    const xRange = [CONFIG.DIMS.margin.x, CONFIG.DIMS.width - CONFIG.DIMS.margin.x]
    this.xScale = scaleBand()
      .domain(range(11))
      .range(xRange)

    this.xScaleLinear = scaleLinear()
      .domain([0, 11])
      .range([xRange])
    this.xScaleAvg = scaleLinear()
      .domain([0, 11])
      .range(xRange)
    // color scale

  }

  /* Define Scales & Axes */

  // y Scales
  yScale = (d, i) => CONFIG.DIMS.height - CONFIG.DIMS.margin.y - SPACING.ySpaceMultiplier * getIndex(d, this.nodeData, this.currentColumn) - SPACING.axisHeight - SPACING.ySpaceMultiplier;
  yScaleArea = (d) => -(SPACING.ySpaceMultiplier + SPACING.dotPadding) * d;


  /* Toggle Handler */
  // note - using arrow function's lexical binding to bind "this" context to DotChart
  // otherwise "this" would point to the Toggle instance
  handleToggle = (toggleVal) => {
    //reset currentColumn classmember variable
    this.currentColumn = this.toggleVals.find((tVal) => tVal.val === toggleVal).text
    // draw chart again to transition
    this.draw(true)
  }

  handleFilter = (selectedOption) => {
    // reset control classmember variable
    this.control = selectedOption
    // draw chart again to transition
    this.draw(false)
  }
  /* Things that only happen once */
  init() {

    // setup toggle
    this.toggleWrapper = this.selection
      .append('div')
      .attr('class', `${C.TOGGLE}-${C.WRAPPER}`)

    this.toggle = new Toggle(this.toggleWrapper,
      this.toggleVals,
      TOGGLE_VALS[0].val,
      this.handleToggle);

    // setup dropdown
    this.dropDownWrapper = this.selection
      .append('div')
      .attr('class', `${C.FILTER}-${C.WRAPPER}`)


    this.gradientWrapper = this.selection
      .append('div')
      .attr('class', `${C.GRADIENT}-${C.WRAPPER}`)
      .style('width', `${CONFIG.DIMS.width}px`)
      .style("transform", `translate(0,${CONFIG.DIMS.height - CONFIG.DIMS.margin.y - SPACING.axisHeight}px)`)
      .style("opacity", 0)
      .call(div => div.transition()
        .duration(CONFIG.DURATION)
        .style("opacity", 0.5))


    new Gradient(this.gradientWrapper,
      50, COLORS.champagne, COLORS.green, CONFIG.DIMS.width - 2 * CONFIG.DIMS.margin.x, SPACING.axisHeight);

    this.svg = this.selection
      .append('svg')
      .attr("id", "svg")
      .attr("width", CONFIG.DIMS.width)
      .attr("height", CONFIG.DIMS.height)
      .append('g')

  }

  /* Things that need to update */
  // @param t_type: transition type (bool); determines if transition is toggle or dropdown shift
  draw(t_type = true) {

    // console.log(largestRemainderNormalizedPassImportance(this.data, this.control))
    // console.log(dataMap(this.data))
    // update data
    this.nodeData = generateDataVals(this.data, this.control)
    this.areaData = getAreaData(this.data, this.currentColumn, this.control)
    this.averages = getAverages(this.data, this.currentColumn, this.control)
    // redraw
    this.draw_nodes(this.nodeData, t_type)
    this.draw_area(this.areaData)
    this.draw_dropdown()
  }

  draw_dropdown() {
    this.dropDown = new DropDown(this.dropDownWrapper,
      this.dropDownVals,
      this.control,
      this.handleFilter);
  }

  draw_axis() {
    /* Define Axis */
    const xAxis = axisTop(this.xScale).tickSizeOuter(0)

    /* Append Axis Group */
    this.svg
      .selectAll(`g.${C.X_AXIS}`)
      .data([0])
      .join("g")
      .attr("class", `${C.X_AXIS}`)
      .attr("transform", `translate(0,${CONFIG.DIMS.height - CONFIG.DIMS.margin.y})`)
      .call(xAxis)
      .transition()
      .duration(CONFIG.DURATION)
      .attr("opacity", 1)

    /* Append Axis Text */
    this.svg
      .selectAll(`text.${C.LABEL}`)
      .data(CONFIG.X_DOMAIN)
      .join("text")
      .attr("class", `${C.LABEL}`)
      .text((d) => d)
      .attr(
        "transform",
        (_, i) =>
          `translate(${i ? CONFIG.DIMS.width - 4.5 * CONFIG.DIMS.margin.x : CONFIG.DIMS.margin.x},
            ${CONFIG.DIMS.height - SPACING.ySpaceMultiplier
          })`
      )
      .attr("opacity", 0)
      .transition()
      .duration(CONFIG.DURATION)
      .attr("opacity", 1)
  }

  draw_nodes(data, t_type = true) {
    this.nodes = this.svg
      .selectAll(`rect.${C.NODE}`)
      .data(data, (d) => d.id)
      .join(enter => {
        const nodes =
          enter.append('rect')
            .style("opacity", 0)
            .attr("x", (d) => this.xScale(d[this.currentColumn]) + this.xScale.bandwidth() / 2 - 3.5)
            .attr("y", this.yScale(0))
            .call(node => node
              .transition()
              .delay(CONFIG.DELAY)
              .duration(CONFIG.DURATION)
              .attr("y", this.yScale)
              .style("opacity", 0.5))
        return nodes
      }, update => {

        update
          .transition()
          .delay(0)
          .duration(0)
          .style('opacity', t_type ? 0.5 : 0)
          .transition()
          .ease(easePolyInOut)
          .delay(t_type ? CONFIG.TIME_SHT : CONFIG.DELAY)
          .duration(t_type ? CONFIG.DURATION : CONFIG.TIME_SHT)
          .attr("x", (d) => this.xScale(d[this.currentColumn]) + this.xScale.bandwidth() / 2 - 3.5)
          .attr("y", this.yScale)
          .transition()
          .duration(CONFIG.TIME_SHT)
          .style("opacity", 0.5)

        return update
      })
      .attr("class", `${C.NODE}`)
      .attr("rx", CONFIG.RADIUS)
      .attr("height", 2 * CONFIG.RADIUS)
      .attr("width", 2 * CONFIG.RADIUS)

  }

  draw_area(areaData) {
    const areaGenerator =
      area()
        .curve(curveCardinal)
        .x(([x]) => this.xScale(x))
        .y1(([, y]) => this.yScaleArea(y))
        .y0(this.yScaleArea(0));

    this.svg
      .selectAll(`path.${C.AREA}`)
      .data([areaData])
      .join(enter => {
        const area = enter
          .append("path")
          .attr("fill-opacity", 0)
          // append at y = 0
          .attr("d", (d) => areaGenerator(d.map(([x]) => [x, 0])))
          .call(enter =>
            // on enter, transition up to y data position and fade in
            enter
              .transition()
              .delay(CONFIG.DELAY)
              .duration(CONFIG.DURATION)
              .attr("d", areaGenerator)
              .attr("fill-opacity", 0.04)
          )
        return area
      },
        update => {
          update
            .transition()
            .duration(CONFIG.DURATION)
            .attr("d", areaGenerator)

          return update
        })
      .attr("class", `${C.AREA}`)
      .attr("transform", `translate(${CONFIG.DIMS.margin.x + 5.5}, ${CONFIG.DIMS.height - CONFIG.DIMS.margin.y - SPACING.axisHeight})`);
  }

}